import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useMemo } from 'react';

import { queryCurrentUser, queryOrganizationById, queryProjectById } from '~/services/authorization/authorization.ts';
import type { FeatureGate } from '~/services/authorization/types.ts';

const APP_AUDIENCE = 'External' as const;

const flagKey = (gate: FeatureGate): string => `${gate.key}|${gate.scope}`;

export function useFeatureGuard() {
  const { project: projectId = '' } = useParams({ strict: false });
  const { data: user, isLoading: isLoadingUser } = useQuery(queryCurrentUser());
  const { data: project, isLoading: isLoadingProject } = useQuery(queryProjectById(projectId));
  const { data: org, isLoading: isLoadingOrg } = useQuery(queryOrganizationById(user?.organizationId ?? ''));

  const flags = useMemo(() => {
    if (!user || !org) {
      return new Set<string>();
    }

    const set = new Set<string>();
    const featureSources = [
      ...org.features,
      ...(project?.features ?? []),
    ];
    const permissionSources = [
      ...org.permissions,
      ...(project?.permissions ?? []),
    ];

    for (const f of featureSources) {
      if (f.audience !== APP_AUDIENCE)
        continue;
      set.add(`${f.key}|${f.scope}`);
    }
    for (const p of permissionSources) {
      if (p.audience !== APP_AUDIENCE)
        continue;
      set.add(`${p.key}|${p.scope}`);
    }
    return set;
  }, [user, org, project]);

  const hasFeature = useMemo(() => {
    return (gate: FeatureGate): boolean => flags.has(flagKey(gate));
  }, [flags]);

  const hasSomeFeature = useMemo(() => {
    return (gates: FeatureGate[]): boolean => {
      return gates.length === 0 || gates.some(gate => hasFeature(gate));
    };
  }, [hasFeature]);

  const hasAllFeatures = useMemo(() => {
    return (gates: FeatureGate[]): boolean => {
      return gates.every(gate => hasFeature(gate));
    };
  }, [hasFeature]);

  return {
    hasFeature,
    hasSomeFeature,
    hasAllFeatures,
    isLoading: isLoadingUser || isLoadingProject || isLoadingOrg,
    flags,
  };
}
